// Pages du portail FFM — wrappers des composants existants
// Chaque page injecte le contexte de session et applique le thème FFM

import { useState } from 'react';
import FFMCommandes  from './FFMCommandes';
import FFMPilotes    from './FFMPilotes';
import FFMProduits   from './FFMProduits';
import FFMOperateurs from './FFMOperateurs';
import FFMProfils    from './FFMProfils';
import { useOutletContext } from 'react-router-dom';

// ─── En-tête de page réutilisable ────────────────────────────────────────────
function PageHeader({ emoji, titre, description }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      marginBottom: '24px',
      paddingBottom: '20px',
      borderBottom: '1px solid #D0D8EE',
    }}>
      <div style={{
        width: '44px',
        height: '44px',
        backgroundColor: '#003189',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
        flexShrink: 0,
      }}>
        {emoji}
      </div>
      <div>
        <h1 style={{
          margin: 0,
          fontSize: '20px',
          fontWeight: '700',
          color: '#1a1a2e',
          letterSpacing: '-0.3px',
        }}>
          {titre}
        </h1>
        <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6b7280' }}>
          {description}
        </p>
      </div>
    </div>
  );
}

// ─── Pages ───────────────────────────────────────────────────────────────────

export function PortalCommandes() {
  useOutletContext(); // accès session si besoin futur
  return (
    <div>
      <PageHeader
        emoji="📋"
        titre="Commandes"
        description="Suivi et gestion des commandes FFM"
      />
      <FFMCommandes />
    </div>
  );
}

export function PortalPilotes() {
  useOutletContext();
  return (
    <div>
      <PageHeader
        emoji="👤"
        titre="Pilotes"
        description="Gestion des pilotes et de leurs informations"
      />
      <FFMPilotes />
    </div>
  );
}

export function PortalProduits() {
  useOutletContext();
  return (
    <div>
      <PageHeader
        emoji="📦"
        titre="Produits"
        description="Catalogue des produits disponibles"
      />
      <FFMProduits />
    </div>
  );
}

export function PortalOperateurs() {
  useOutletContext();
  return (
    <div>
      <PageHeader
        emoji="🔧"
        titre="Opérateurs"
        description="Gestion des comptes opérateurs FFM"
      />
      <FFMOperateurs />
    </div>
  );
}

export function PortalProfils() {
  useOutletContext();
  return (
    <div>
      <PageHeader
        emoji="🎫"
        titre="Profils"
        description="Gestion des profils et gabarits de commandes"
      />
      <FFMProfils />
    </div>
  );
}

// ─── Guide d'utilisation ─────────────────────────────────────────────────────

const SECTIONS_AIDE = [
  {
    role: null, // accessible à tous
    emoji: '🏍️',
    titre: 'La plateforme Custom Lab FFM',
    contenu: [
      'Custom Lab FFM est la plateforme de personnalisation des équipements des équipes de France de Motocyclisme.',
      'Elle permet de gérer les commandes de personnalisation, de valider les visuels (BAT) avec les pilotes, et de suivre la production jusqu\'à l\'expédition.',
      'Le processus repose sur une validation en 2 étapes : le pilote valide d\'abord son visuel, puis l\'opérateur FFM donne sa validation finale avant lancement de la production.',
    ],
  },
  {
    role: 'commandes',
    emoji: '📋',
    titre: 'Les commandes',
    contenu: [
      'Une commande regroupe un ou plusieurs produits à personnaliser pour un pilote ou une délégation.',
      'Pour créer une commande : allez dans "Commandes" → bouton "Nouvelle commande". Renseignez la référence, le pilote, les produits et l\'adresse de livraison.',
      'Chaque produit d\'une commande peut avoir un BAT associé. Le statut évolue de "En attente" → "BAT envoyé" → "BAT validé" → "En production" → "Expédié".',
      'Une fois les produits prêts, vous pouvez renseigner le numéro de suivi et la date d\'expédition pour clôturer la commande.',
    ],
  },
  {
    role: 'commandes',
    emoji: '🎨',
    titre: 'Le processus BAT (Bon À Tirer)',
    contenu: [
      'Le BAT est le visuel final du produit personnalisé, soumis à validation avant production.',
      'Étape 1 — Envoi au pilote : depuis une commande, cliquez sur "Envoyer BAT". Le pilote reçoit un email avec un lien unique pour visualiser et approuver ou refuser le visuel.',
      'Étape 2 — Validation opérateur : une fois le pilote ayant validé, vous recevez automatiquement un email pour effectuer votre validation finale.',
      'En cas de refus : une nouvelle version du BAT doit être préparée et renvoyée. L\'historique des versions est conservé.',
      '⚠️ La production ne démarre qu\'après les 2 validations (pilote + opérateur FFM).',
    ],
  },
  {
    role: 'pilotes',
    emoji: '👤',
    titre: 'Les pilotes',
    contenu: [
      'La section Pilotes liste l\'ensemble des pilotes référencés sur la plateforme.',
      'Vous pouvez ajouter un nouveau pilote, modifier ses informations (nom, prénom, email, discipline, numéro de dossard).',
      'L\'email du pilote est important : c\'est l\'adresse à laquelle sont envoyés les liens de validation BAT.',
    ],
  },
  {
    role: 'produits',
    emoji: '📦',
    titre: 'Les produits',
    contenu: [
      'La section Produits liste le catalogue des articles disponibles à la personnalisation (maillots, pantalons, casques, etc.).',
      'Chaque produit a un label, une référence, et des tailles disponibles. Ces informations sont utilisées lors de la création des commandes.',
    ],
  },
  {
    role: 'operateurs',
    emoji: '🔧',
    titre: 'Les opérateurs',
    contenu: [
      'Les opérateurs sont les personnes ayant accès à cet espace de gestion.',
      'Chaque opérateur est rattaché à un profil qui définit les onglets auxquels il a accès.',
      'Pour ajouter un opérateur : renseignez ses informations et sélectionnez son profil. Il recevra son accès par email OTP (code à usage unique).',
    ],
  },
  {
    role: 'profils',
    emoji: '🎫',
    titre: 'Les profils d\'accès',
    contenu: [
      'Les profils définissent les droits d\'accès des opérateurs (quels onglets sont visibles).',
      'Un profil "admin" donne accès à tout. Des profils spécialisés peuvent être créés pour limiter l\'accès à certaines fonctions.',
    ],
  },
  {
    role: null,
    emoji: '❓',
    titre: 'FAQ — Questions fréquentes',
    contenu: [
      '🔗 Le lien BAT du pilote est expiré ou ne fonctionne plus → Renvoyez un BAT depuis la commande. Chaque envoi génère un nouveau lien unique.',
      '📧 Le pilote n\'a pas reçu l\'email → Vérifiez l\'adresse email dans sa fiche pilote. Vous pouvez renvoyer le BAT.',
      '⏳ Le pilote ne valide pas → Rappelez-le directement. En attendant, l\'opérateur peut consulter le BAT en "lecture seule" via son propre lien.',
      '🔄 Comment annuler une validation → La validation est définitive. Si un problème est constaté après validation, créez une nouvelle version du BAT.',
      '🔑 Code de connexion non reçu → Vérifiez vos spams. Le code est valable 10 minutes. Si le problème persiste, contactez votre responsable FFM.',
    ],
  },
];

export function PortalAide() {
  const { session } = useOutletContext();
  const roles = session?.roles || [];
  const isAdmin = roles.includes('admin');

  const sectionsVisibles = SECTIONS_AIDE.filter(
    (s) => s.role === null || isAdmin || roles.includes(s.role)
  );

  return (
    <div>
      <PageHeader
        emoji="❓"
        titre="Aide & Guide d'utilisation"
        description="Tout ce qu'il faut savoir pour utiliser la plateforme Custom Lab FFM"
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '860px' }}>
        {sectionsVisibles.map((section, i) => (
          <SectionAide key={i} section={section} />
        ))}
      </div>
    </div>
  );
}

function SectionAide({ section }) {
  const [ouvert, setOuvert] = useState(false);

  return (
    <div style={{
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,49,137,0.06)',
      overflow: 'hidden',
      border: '1px solid #D0D8EE',
    }}>
      <button
        onClick={() => setOuvert(!ouvert)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 24px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '22px' }}>{section.emoji}</span>
          <span style={{ fontWeight: '700', fontSize: '15px', color: '#1a1a2e' }}>{section.titre}</span>
        </div>
        <span style={{ color: '#003189', fontSize: '18px', fontWeight: '700', flexShrink: 0 }}>
          {ouvert ? '−' : '+'}
        </span>
      </button>

      {ouvert && (
        <div style={{ padding: '0 24px 20px', borderTop: '1px solid #f0f4ff' }}>
          {section.contenu.map((ligne, j) => (
            <div key={j} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              padding: '10px 0',
              borderBottom: j < section.contenu.length - 1 ? '1px solid #f0f4ff' : 'none',
            }}>
              <span style={{ color: '#003189', fontWeight: '700', marginTop: '2px', flexShrink: 0 }}>→</span>
              <p style={{ margin: 0, fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>{ligne}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
